/**
 * add_entity_field — a confirm-lane tool.
 *
 * Navigates to the entity, applies one or more fields using the same rules the entity chat uses
 * (ADR 0009), then marks the page's own Save or Update and waits for the builder's click. Partial
 * application: a refused field is named in the answer with the form's own message, and the rest apply.
 *
 * A link field's `data` copy is never written or filled here; the entity page owns it.
 *
 * ⚠ `import type` only for entity.model — see rules/entity-form-rules.ts.
 */
import type { Entity } from '../../../model/entity.model';
import type { EntityChatPage } from '../../../dashboard/entity/zs-entity/chat/entity-chat-session';
import type { EntityPageBridgeService } from '../entity-page-bridge.service';
import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';
import type { EntityEdit } from '../../../dashboard/entity/zs-entity/zs-entity.types';
import { navigateFirst, NavigateDeps } from './navigate.tool';
import { applyFieldEdit, linkTargetById } from '../../../dashboard/entity/zs-entity/apply/apply-edit';
import { ConfirmWaits, Prefilled, PressToCommitDeps, pressToCommit } from '../actions/confirm-lane';
import { ENTITY_COMMIT, entityPageBusy, saveButtonFor, savedEntitySummary } from '../actions/entity-confirm';
import type { ActionRow } from '../actions/action-table';

// ── Input shape ────────────────────────────────────────────────────────────────────────────────────

export interface AddFieldInput {
  name: string;
  type: string;
  settings?: {
    referenceEntityId?: string;
    description?: string;
  };
}

export interface AddEntityFieldInput {
  entityId: string;
  fields: AddFieldInput[];
}

// ── Result shape ───────────────────────────────────────────────────────────────────────────────────

export interface AppliedField { name: string; type: string }
export interface RefusedField { name: string; reason: string }

export interface AddEntityFieldResult {
  applied: AppliedField[];
  refused: RefusedField[];
}

// ── Dependencies ───────────────────────────────────────────────────────────────────────────────────

export interface AddEntityFieldDeps {
  bridge: Pick<EntityPageBridgeService, 'page'>;
  waits: Pick<ConfirmWaits, 'begin'>;
  highlight: PressToCommitDeps['highlight'];
  navigateDeps: NavigateDeps;
  /** All entities in the project (for reference targets). Typically from the bridge page. */
  getEntities: () => readonly Entity[];
  /** How long to wait for the entity page to open after navigating. */
  pageWaitMs?: number;
}

const PAGE_WAIT_MS = 5000;

// The entity page registers its adapter only after it has rendered, and loads the requested
// entity a moment later still, so a call made from another screen has to wait for both.
async function waitForEntityPage(deps: AddEntityFieldDeps, entityId: string): Promise<EntityChatPage | null> {
  const deadline = Date.now() + (deps.pageWaitMs ?? PAGE_WAIT_MS);
  for (;;) {
    const page = deps.bridge.page;
    const open = page?.open() ?? null;
    if (page && open?.id === entityId) return page;
    if (Date.now() >= deadline) return page && !open ? page : null;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────────────────────────

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

function findEntity(entities: readonly Entity[], entityId: string): Entity | undefined {
  return entities.find(e => e.id === entityId);
}

/** The chat's add_field edit for one requested field; the link is named by id and looked up by id. */
function addFieldEdit(input: AddFieldInput): EntityEdit {
  return {
    op: 'add_field', entity: null, local: null, path: [], field: input.name, newName: null, type: input.type,
    reference: input.settings?.referenceEntityId ?? null, extendedAttributes: false, defaultValue: null,
    defaultMode: null, description: input.settings?.description ?? null, values: null, row: null, rowNumber: null,
  };
}

// A link's id has to come from read_project, so a missing or unknown one is answered with that next step
// before the shared lookup gives its form-worded reason.
function linkInputProblem(input: AddFieldInput, entities: readonly Entity[]): string | null {
  if (input.type !== 'ENTITY_REFERENCE') return null;
  const refId = input.settings?.referenceEntityId;
  if (!refId) return 'A link needs the entity it points to. Set settings.referenceEntityId to a finalized entity\'s id.';
  if (!entities.some(e => e.id === refId)) return `Entity '${refId}' not found. Use read_project to find the entity to link to.`;
  return null;
}

/** Apply one field to `draft` through the entity chat's own add step; the new field's id when it landed. */
function applyOneField(
  draft: Entity,
  input: AddFieldInput,
  page: EntityChatPage,
): { applied: AppliedField; fieldId: string } | { refused: RefusedField } {
  const entities = page.entities();
  const problem = linkInputProblem(input, entities);
  if (problem) return { refused: { name: input.name, reason: problem } };
  const before = draft.data.length;
  const out = applyFieldEdit(draft, addFieldEdit(input), {
    entities, mint: () => page.mint(), nowIso: page.now().toISOString(), linkTarget: linkTargetById,
  });
  if (draft.data.length === before) {
    return { refused: { name: input.name, reason: out.declined.join(' ') || `Couldn't add ${input.name}.` } };
  }
  const field = draft.data[draft.data.length - 1];
  return { applied: { name: field.name, type: field.type }, fieldId: field.id };
}

// The page has to hold exactly what this call put there before the builder is shown a button to press.
function pageMovedReason(
  deps: AddEntityFieldDeps,
  page: EntityChatPage,
  entityId: string,
  fieldIds: readonly string[],
): string | null {
  if (deps.bridge.page !== page) return 'The entity page was closed or reloaded';
  const open = page.open();
  if (open?.id !== entityId) return 'A different entity is open now';
  const ids = new Set(open.data.map(f => f.id));
  if (!fieldIds.every(id => ids.has(id))) return 'The fields this call added are no longer in its unsaved edits';
  return null;
}

// ── Tool factory ───────────────────────────────────────────────────────────────────────────────────

// Was (2026-10-30), when this tool held the save for an approval card and pressed Save itself. The builder's own click
// commits now, so entityPageBusy in actions/entity-confirm.ts took pageBusyReason's place, and the wait reports what saved:
// /** What the entity page has open that the chat also waits on; null when nothing is in the way. */
// function pageBusyReason(page: EntityChatPage, entityName: string): string | null {
//   if (page.fieldFormOpen()) return `The field form is open on the entity page. Ask the builder to finish or close it, then call add_entity_field again for ${entityName}.`;
//   if (page.permissionsOpen()) return `Role permissions is open on the entity page. Ask the builder to finish or close it, then call add_entity_field again for ${entityName}.`;
//   if (page.sampleRowsOpen()) return `Predefined rows is open on the entity page. Ask the builder to finish or close it, then call add_entity_field again for ${entityName}.`;
//   return null;
// }
//
// // The builder can open another entity, undo, or close the page while the card is up, and Save saves
// // whatever is open, so the page must still hold exactly what this call put there.
// function pageMovedReason(
//   deps: AddEntityFieldDeps,
//   page: EntityChatPage,
//   entityId: string,
//   fieldIds: readonly string[],
// ): string | null {
//   if (deps.bridge.page !== page) return 'The entity page was closed or reloaded';
//   const open = page.open();
//   if (open?.id !== entityId) return 'A different entity is open now';
//   const ids = new Set(open.data.map(f => f.id));
//   if (!fieldIds.every(id => ids.has(id))) return 'The fields this call added are no longer in its unsaved edits';
//   return null;
// }
//
// // The page closes the entity once it saves, so a closed entity whose stored copy holds every added field
// // was saved by the builder's own Update.
// function savedByBuilder(page: EntityChatPage, entityId: string, fieldIds: readonly string[]): boolean {
//   const stored = page.entities().find(e => e.id === entityId);
//   if (!stored) return false;
//   const ids = new Set(stored.data.map(f => f.id));
//   return fieldIds.every(id => ids.has(id));
// }
//
//     async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
//       const entityId = input['entityId'] as string;
//       const fields = input['fields'] as AddFieldInput[];
//
//       const entities = deps.getEntities();
//       const entity = findEntity(entities, entityId);
//       if (!entity) {
//         return { outcome: 'refused', refusal: `Entity '${entityId}' not found in this project. Use read_project to find its id.` };
//       }
//
//       const navResult = await navigateFirst('entity', entityId, ctx, deps.navigateDeps);
//       if (navResult.outcome !== 'done') {
//         return navResult;
//       }
//
//       const page = await waitForEntityPage(deps, entityId);
//       if (!page) {
//         return {
//           outcome: 'refused',
//           refusal: `The entity page didn't open ${entity.name}. Ask the builder to open it, then try again.`,
//         };
//       }
//
//       const busy = pageBusyReason(page, entity.name);
//       if (busy) return { outcome: 'refused', refusal: busy };
//
//       const open = page.open();
//       const draft: Entity = clone(open ?? findEntity(page.entities(), entityId) ?? entity);
//
//       const applied: AppliedField[] = [];
//       const refused: RefusedField[] = [];
//       const fieldIds: string[] = [];
//       for (const f of fields) {
//         const result = applyOneField(draft, f, page);
//         if ('refused' in result) {
//           refused.push(result.refused);
//         } else {
//           applied.push(result.applied);
//           fieldIds.push(result.fieldId);
//         }
//       }
//
//       // If nothing applied, return the refusals only — no approval needed
//       if (applied.length === 0) {
//         return {
//           outcome: 'done',
//           changed: [],
//           result: { applied: [], refused } as AddEntityFieldResult,
//         };
//       }
//
//       page.adopt(draft, open ? 'open' : 'existing');
//       const notTaken = pageMovedReason(deps, page, entityId, fieldIds);
//       if (notTaken) {
//         return {
//           outcome: 'refused',
//           refusal: `${notTaken}, so the entity page didn't take the new fields and the builder wasn't asked. Ask the builder to open ${entity.name}, then call add_entity_field again.`,
//         };
//       }
//
//       const approval = await ctx.askApproval({
//         kind: 'held',
//         title: `Save ${entity.name} and build its table`,
//         details: applied.map(f => `Add ${f.name} (${typeLabel(f.type)})`),
//         request: ctx.request,
//       });
//
//       const changedText = applied.map(f => `${f.name} (${typeLabel(f.type)})`).join(', ');
//       if (approval === 'approved') {
//         if (deps.bridge.page === page && page.open() === null) {
//           if (savedByBuilder(page, entityId, fieldIds)) {
//             return {
//               outcome: 'done',
//               changed: [`Added ${changedText} to ${entity.name}; the builder saved it from the entity page`],
//               result: { applied, refused } as AddEntityFieldResult,
//             };
//           }
//           return {
//             outcome: 'refused',
//             refusal: `The builder closed ${entity.name} without saving the new fields, so nothing was saved. Check with the builder, then call add_entity_field again.`,
//           };
//         }
//         const moved = pageMovedReason(deps, page, entityId, fieldIds);
//         if (moved) {
//           return {
//             outcome: 'refused',
//             refusal: `${moved}, so nothing was saved. Check with the builder what ${entity.name} holds now, then call add_entity_field again.`,
//           };
//         }
//         const busyNow = pageBusyReason(page, entity.name);
//         if (busyNow) return { outcome: 'refused', refusal: `Nothing was saved. ${busyNow}` };
//         // save() reports only whether it worked; the page itself shows the builder why it didn't.
//         const saved = await page.save();
//         if (!saved) {
//           return {
//             outcome: 'refused',
//             refusal: `The entity page didn't save ${entity.name}; it shows the builder why. The fields are still in its unsaved edits. Ask the builder what the page says, and save once it is fixed.`,
//           };
//         }
//         return {
//           outcome: 'done',
//           changed: [`Added ${changedText} to ${entity.name}`],
//           result: { applied, refused } as AddEntityFieldResult,
//         };
//       }
//
//       const unsaved: AddEntityFieldResult = { applied, refused, saved: false };
//       if (approval === 'declined') return { outcome: 'declined', by: 'builder', result: unsaved };
//       return { outcome: 'expired', result: unsaved };
//     },

export function makeAddEntityFieldTool(deps: AddEntityFieldDeps): ToolDef {
  return {
    name: 'add_entity_field',
    kind: 'waits',
    description: [
      'Adds one or more fields to an entity, opening the entity first if it isn\'t on screen.',
      'Each field goes through the entity page\'s own rules (name cleanup, reserved and duplicate names,',
      'the types that entity may hold); a field that breaks one is refused with the reason, and the',
      'others still apply.',
      'The fields go into the entity\'s unsaved edits and the page\'s Save or Update is marked for the',
      'builder, who can change them before pressing it. This call waits for that press, and only the',
      'builder can press. The answer reports what was really saved, or how the wait ended without a save;',
      'it always lists the fields applied and refused, and unsaved ones stay on the screen for the builder.',
      'A link field (ENTITY_REFERENCE) needs settings.referenceEntityId, a finalized entity\'s id; an',
      'entity may link to itself, and a local entity can\'t hold links.',
      'Zavo has no "required" or "unique" field setting. Asked to make a field required, add it as a',
      'normal field and tell the builder the check goes in the Blockly logic of the page that saves the',
      'record, which shows the message. Unique is not offered.',
      'Returns the fields applied and refused, by name and type, never record values.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        entityId: { type: 'string', description: 'The entity to add fields to.' },
        fields: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The field name.' },
              type: {
                type: 'string',
                description: 'The field type (STRING, NUMBER, BOOLEAN, ENTITY_REFERENCE, ATTACHMENT, TIMESTAMP, GEOMETRY, ARRAY_OF_OBJECT).',
              },
              settings: {
                type: 'object',
                properties: {
                  referenceEntityId: { type: 'string', description: 'For ENTITY_REFERENCE: the target entity id.' },
                  description: { type: 'string', description: 'Field description.' },
                },
                additionalProperties: false,
              },
            },
            required: ['name', 'type'],
            additionalProperties: false,
          },
          description: 'The fields to add.',
        },
      },
      required: ['entityId', 'fields'],
      additionalProperties: false,
    },

    run: (input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> =>
      pressToCommit({
        waits: deps.waits,
        highlight: deps.highlight,
        commit: ENTITY_COMMIT,
        prefill: () => prefillFields(deps, input, ctx),
      })(input, ctx, ADD_FIELD_ROW),
  };
}

// The tool isn't a row of the action table, and every prefill names its own button, so only the label is read.
const ADD_FIELD_ROW: ActionRow = {
  id: 'add-entity-field', label: 'add_entity_field', screen: 'entity', lane: 'confirm', fields: [],
  commit: { kind: 'own-tool', tool: 'add_entity_field' },
};

async function prefillFields(deps: AddEntityFieldDeps, input: Record<string, unknown>, ctx: ToolContext): Promise<Prefilled> {
  const entityId = input['entityId'] as string;
  const fields = input['fields'] as AddFieldInput[];

  const entities = deps.getEntities();
  const entity = findEntity(entities, entityId);
  if (!entity) return { refused: `Entity '${entityId}' not found in this project. Use read_project to find its id.` };

  const navResult = await navigateFirst('entity', entityId, ctx, deps.navigateDeps);
  if (navResult.outcome !== 'done') return { answer: navResult };

  const page = await waitForEntityPage(deps, entityId);
  if (!page) return { refused: `The entity page didn't open ${entity.name}. Ask the builder to open it, then try again.` };

  const busy = entityPageBusy(page, `call add_entity_field again for ${entity.name}`);
  if (busy) return { refused: busy };

  const open = page.open();
  const draft: Entity = clone(open ?? findEntity(page.entities(), entityId) ?? entity);

  const applied: AppliedField[] = [];
  const refused: RefusedField[] = [];
  const fieldIds: string[] = [];
  for (const f of fields) {
    const result = applyOneField(draft, f, page);
    if ('refused' in result) {
      refused.push(result.refused);
    } else {
      applied.push(result.applied);
      fieldIds.push(result.fieldId);
    }
  }

  // Nothing landed on the page, so there is nothing for the builder to press.
  if (applied.length === 0) {
    return { answer: { outcome: 'done', changed: [], result: { applied: [], refused } as AddEntityFieldResult } };
  }

  page.adopt(draft, open ? 'open' : 'existing');
  const notTaken = pageMovedReason(deps, page, entityId, fieldIds);
  if (notTaken) {
    return { refused: `${notTaken}, so the entity page didn't take the new fields and the builder wasn't asked. Ask the builder to open ${entity.name}, then call add_entity_field again.` };
  }

  return { button: saveButtonFor(draft), also: { applied, refused }, describe: savedEntitySummary };
}
